import { alpha, SxProps, useIsDarkMode, useTheme } from '@chronosphereio/chrono-ui';
// This is a custom version of the Checkbox component that doesn't match the Spotlight designs
// so it's okay to import directly from MUI instead of using the Chrono UI component.
// eslint-disable-next-line no-restricted-imports
import { Checkbox, CheckboxProps, darken, lighten } from '@mui/material';
import {
  CheckboxBlank as CheckboxBlankIcon,
  CheckboxBlankOutline as CheckboxBlankOutlineIcon,
  MinusBox as MinusBoxIcon,
} from 'mdi-material-ui';
import { forwardRef, memo } from 'react';
import { CheckboxDashedIcon } from './CheckboxDashedIcon';
import { CheckboxDashedOutlineIcon } from './CheckboxDashedOutlineIcon';
import { CheckboxStripedIcon } from './CheckboxStripedIcon';
import { CheckboxStripedOutlineIcon } from './CheckboxStripedOutlineIcon';
import { LegendMarkerVariant, safeColorManipulate } from './legend-model';

export interface LegendCheckboxProps
  extends Pick<CheckboxProps, 'checked' | 'indeterminate' | 'onChange' | 'onKeyDown' | 'tabIndex'> {
  color?: string;
  sx?: Omit<SxProps, 'color' | 'padding' | 'background' | 'borderRadius'>;
  variant?: LegendMarkerVariant;
}

export const LegendCheckbox = memo(
  forwardRef<HTMLButtonElement, LegendCheckboxProps>(function LegendCheckbox(props: LegendCheckboxProps, ref) {
    const { color, sx, variant, ...otherProps } = props;

    const isDarkMode = useIsDarkMode();

    const { theme } = useTheme();
    const fallbackColor = theme.palette.primary.main;
    const legendItemColor = color ?? fallbackColor;
    let icon = <CheckboxBlankOutlineIcon />;
    let checkedIcon = <CheckboxBlankIcon />;
    if (variant === 'striped') {
      icon = <CheckboxStripedOutlineIcon />;
      checkedIcon = <CheckboxStripedIcon />;
    } else if (variant === 'dashed') {
      icon = <CheckboxDashedOutlineIcon />;
      checkedIcon = <CheckboxDashedIcon />;
    }

    return (
      <Checkbox
        ref={ref}
        size={'small'}
        {...otherProps}
        // Icons
        icon={icon}
        checkedIcon={checkedIcon}
        indeterminateIcon={<MinusBoxIcon />}
        // Setting checked to "false" for indeterminate enables us to "trick"
        // MUI into doing what we want for indeterminate header checkboxes (select all) instead of
        // what it currently does (select none).
        // This can be removed when MUI fixes the following issue, and we're able to update to the version with the fix.
        // https://github.com/mui/mui-x/issues/7753
        checked={props.indeterminate ? false : props.checked}
        sx={{
          ...sx,
          color: color ? `${color} !important` : undefined,

          // Hover state styles using valid color check
          ':hover': {
            color: `${safeColorManipulate(legendItemColor, fallbackColor, () => (isDarkMode ? lighten(legendItemColor, 0.5) : darken(legendItemColor, 0.5)))} !important`,
            background: safeColorManipulate(legendItemColor, fallbackColor, () => alpha(legendItemColor, 0.25)),
            borderRadius: '4px',
          },

          // Focus state styles using valid color check
          '&.Mui-focusVisible': {
            background: safeColorManipulate(legendItemColor, fallbackColor, () => alpha(legendItemColor, 0.5)),
            borderRadius: '4px',
          },

          padding: (theme) => theme.spacing(0.25),
          '& .MuiSvgIcon-root': { fontSize: 16 },
        }}
      />
    );
  })
);
